import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { API_MAX_ACTIVE_TOKENS, API_RATE_LIMIT_PER_MINUTE } from './apiBaseUrl';
import { Panel } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiUsageStripProps {
  activeTokens: number;
  /** Daily browse credits from the caller's plan, when the token carries them. */
  dailyBrowseCredits?: number;
}

/**
 * The three numbers that decide whether a request succeeds: how fast you may
 * call, how many keys you may hold, and how much browsing your plan buys.
 */
const ApiUsageStripComponent: React.FC<ApiUsageStripProps> = ({ activeTokens, dailyBrowseCredits }) => {
  const intl = useIntl();
  const usedShare = Math.min(100, Math.round((activeTokens / API_MAX_ACTIVE_TOKENS) * 100));

  return (
    <Panel className={styles.usage}>
      <div className={styles.usageCell}>
        <div className={styles.usageKey}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.usage.rate', defaultMessage: 'Rate limit' })}
        </div>
        <div className={styles.usageValue}>
          {API_RATE_LIMIT_PER_MINUTE}
          <small>
            {' '}
            {intl.formatMessage({ id: 'app.sml.settings.apiAccess.usage.perMinute', defaultMessage: 'req / min' })}
          </small>
        </div>
        <div className={styles.usageCaption}>
          {intl.formatMessage({
            id: 'app.sml.settings.apiAccess.usage.rateCaption',
            defaultMessage: 'Per user, shared with MCP tools. Over it you get TOO_MANY_REQUESTS.',
          })}
        </div>
      </div>

      <div className={styles.usageCell}>
        <div className={styles.usageKey}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.usage.tokens', defaultMessage: 'Active tokens' })}
        </div>
        <div className={styles.usageValue}>
          {activeTokens}
          <small> / {API_MAX_ACTIVE_TOKENS}</small>
        </div>
        <div className={styles.meter}>
          <i style={{ width: `${usedShare}%` }} />
        </div>
      </div>

      <div className={styles.usageCell}>
        <div className={styles.usageKey}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.usage.credits', defaultMessage: 'Browse credits' })}
        </div>
        <div className={styles.usageValue}>
          {dailyBrowseCredits ?? '—'}
          <small>
            {' '}
            {intl.formatMessage({ id: 'app.sml.settings.apiAccess.usage.perDay', defaultMessage: '/ day' })}
          </small>
        </div>
        <div className={styles.usageCaption}>
          {intl.formatMessage({
            id: 'app.sml.settings.apiAccess.usage.creditsCaption',
            defaultMessage: 'Spent only on artists, tracks and playlists you do not already follow.',
          })}
        </div>
      </div>
    </Panel>
  );
};

export const ApiUsageStrip = TypedMemo(ApiUsageStripComponent);
